import { useUiStore } from '@/shared/hooks/use-shallow-store';
import { useT } from '@/shared/lib/i18n';
import { LayoutPref } from '@/shared/lib/layout';
import { AdaptiveDialog } from './AdaptiveDialog';
import { BrandMark } from './BrandMark';
import { LanguageCta } from './LanguageCta';
import { PhaseTag } from './PhaseTag';
import { Pin } from './Pin';

export type SettingsSheetProps = {
  open: boolean;
  onClose: () => void;
};

export const SettingsSheet = ({ open, onClose }: SettingsSheetProps) => {
  const t = useT();
  const muted = useUiStore((s) => s.muted);
  const toggleMute = useUiStore((s) => s.toggleMute);
  const volume = useUiStore((s) => s.volume);
  const setVolume = useUiStore((s) => s.setVolume);
  const layoutPref = useUiStore((s) => s.layoutPref);
  const setLayoutPref = useUiStore((s) => s.setLayoutPref);

  return (
    <AdaptiveDialog
      open={open}
      onClose={onClose}
      ariaLabel={t('settings.title')}
      closeLabel={t('settings.close')}
      surface="bare"
      hideHandle
    >
      <div className="paper-dossier relative flex flex-col gap-5 px-7 py-6 text-ink">
        <Pin left={28} top={-6} />
        <Pin left={'calc(100% - 28px)'} top={-6} />

        <header className="flex flex-col gap-1">
          <PhaseTag size={11} color="rgba(0,0,0,.5)">
            {t('settings.title')}
          </PhaseTag>
          <BrandMark size={'clamp(36px, 4vw, 46px)'} className="mt-1">
            {t('settings.title')}
          </BrandMark>
        </header>

        <section className="flex flex-col gap-3">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('settings.section.language')}
          </PhaseTag>
          <LanguageCta size="sm" />
        </section>

        <section className="flex flex-col gap-3">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('settings.section.layout')}
          </PhaseTag>
          <div className="flex gap-2">
            <LayoutPill
              active={layoutPref === LayoutPref.Auto}
              onClick={() => setLayoutPref(LayoutPref.Auto)}
              label={t('settings.layout.auto')}
            />
            <LayoutPill
              active={layoutPref === LayoutPref.Mobile}
              onClick={() => setLayoutPref(LayoutPref.Mobile)}
              label={t('settings.layout.mobile')}
            />
            <LayoutPill
              active={layoutPref === LayoutPref.Desktop}
              onClick={() => setLayoutPref(LayoutPref.Desktop)}
              label={t('settings.layout.desktop')}
            />
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <PhaseTag size={11} color="var(--color-ink-soft)" tracking={0.2}>
            {t('settings.section.audio')}
          </PhaseTag>
          <label className="flex items-center justify-between gap-4 border-ink/85 border-b py-2">
            <span className="font-type text-[15px] text-ink tracking-[0.04em]">
              {t('settings.audio.mute')}
            </span>
            <input
              type="checkbox"
              checked={muted}
              onChange={toggleMute}
              className="h-5 w-5 accent-ink"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 flex items-center justify-between font-type text-[15px] text-ink tracking-[0.04em]">
              <span>{t('settings.audio.volume')}</span>
              <span className="font-mono text-[10px] text-ink/55 tabular-nums tracking-[0.06em]">
                {Math.round(volume * 100)}%
              </span>
            </span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              disabled={muted}
              onChange={(e) => setVolume(Number(e.target.value))}
              aria-label={t('a11y.audio.volume_label')}
              className="w-full accent-ink disabled:opacity-50"
            />
          </label>
        </section>
      </div>
    </AdaptiveDialog>
  );
};

const LayoutPill = ({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={
      active
        ? 'border-[1.5px] border-ink bg-ink px-4 py-2 font-display font-extrabold text-[12px] text-cream uppercase tracking-[0.1em]'
        : 'border-[1.5px] border-ink bg-transparent px-4 py-2 font-display font-extrabold text-[12px] text-ink uppercase tracking-[0.1em] hover:bg-ink/10'
    }
  >
    {label}
  </button>
);
